/**
 * Prompt Health Pipeline
 * 
 * Orchestrates the full cycle:
 *   Scene State → Token Extraction → Conflict Detection
 *   → Fix Strategy → State Update → Prompt Rebuild → Re-analyze
 * 
 * Prevents infinite loops with a max-pass limit.
 */

import type { CanvasElement, GlobalSettings, AspectRatio } from '@/lib/engineTypes';
import type { PromptIssue } from '@/lib/promptInlineAnalyzer';
import { analyzeInlinePromptIssues } from '@/lib/promptInlineAnalyzer';
import {
  extractTokens,
  detectTokenConflicts,
  type TokenConflict,
} from './tokenRegistry';
import {
  autoRepairAll,
  fixTokenConflict,
  fixPromptIssue,
  type SceneFixContext,
  type SceneFixResult,
  type FixableIssueId,
  TOKEN_GROUP_TO_ISSUE_ID,
} from './fixStrategyEngine';

const MAX_CONVERGENCE_PASSES = 3;

// ─── Health Report ───

export interface PromptHealthReport {
  tokenConflicts: TokenConflict[];
  textIssues: PromptIssue[];
  totalIssues: number;
  isClean: boolean;
}

/**
 * Analyze the current scene for all issues (token conflicts + text issues).
 */
export const analyzeSceneHealth = (
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  aspectRatio: string,
  stylePreset: string,
  promptText: string,
): PromptHealthReport => {
  const tokens = extractTokens(elements, globalSettings, aspectRatio, stylePreset);
  const tokenConflicts = detectTokenConflicts(tokens);

  const textIssues = analyzeInlinePromptIssues(promptText)
    .filter((i) => i.type !== 'valid');

  const totalIssues = tokenConflicts.length + textIssues.length;

  return {
    tokenConflicts,
    textIssues,
    totalIssues,
    isClean: totalIssues === 0,
  };
};

// ─── Single Issue Fix ───

export interface SingleFixRequest {
  /** If fixing a token conflict, provide the group */
  tokenGroup?: string;
  /** If fixing a text issue, provide the PromptIssue */
  textIssue?: PromptIssue;
}

/**
 * Fix a single issue by mutating scene state.
 * Returns the new scene context after the fix.
 */
export const fixSingleIssue = (
  ctx: SceneFixContext,
  request: SingleFixRequest,
  healthReport: PromptHealthReport,
): SceneFixResult => {
  if (request.tokenGroup) {
    const conflict = healthReport.tokenConflicts.find(
      (c) => c.group === request.tokenGroup,
    );
    if (conflict) {
      return fixTokenConflict(ctx, conflict);
    }
  }

  if (request.textIssue) {
    return fixPromptIssue(ctx, request.textIssue);
  }

  // No fix applicable
  return {
    ...ctx,
    resolved: [],
    locksApplied: [],
  };
};

// ─── Fix All (with convergence) ───

export interface FixAllResult extends SceneFixResult {
  passes: number;
  converged: boolean;
}

/**
 * AUTO_REPAIR pipeline: fix all issues with up to MAX_CONVERGENCE_PASSES
 * to ensure the fix itself didn't introduce new issues.
 */
export const fixAllIssues = (
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  aspectRatio: AspectRatio,
  stylePreset: string,
  sceneDescription: string,
  promptText: string,
): FixAllResult => {
  let ctx: SceneFixContext = {
    elements: JSON.parse(JSON.stringify(elements)),
    globalSettings: { ...globalSettings },
    aspectRatio,
    stylePreset,
    sceneDescription,
  };

  const allResolved: FixableIssueId[] = [];
  let passes = 0;

  for (let pass = 0; pass < MAX_CONVERGENCE_PASSES; pass++) {
    passes = pass + 1;

    const health = analyzeSceneHealth(
      ctx.elements,
      ctx.globalSettings,
      ctx.aspectRatio,
      ctx.stylePreset,
      promptText,
    );

    if (health.isClean) {
      return {
        ...ctx,
        resolved: [...new Set(allResolved)],
        locksApplied: [],
        passes,
        converged: true,
      };
    }

    const result = autoRepairAll(ctx, health.tokenConflicts, health.textIssues);
    ctx = result;
    allResolved.push(...result.resolved);

    // Check if we made progress
    const postHealth = analyzeSceneHealth(
      ctx.elements,
      ctx.globalSettings,
      ctx.aspectRatio,
      ctx.stylePreset,
      promptText,
    );

    if (postHealth.totalIssues >= health.totalIssues) {
      // No progress — stop to prevent infinite loop
      break;
    }
  }

  return {
    ...ctx,
    resolved: [...new Set(allResolved)],
    locksApplied: [],
    passes,
    converged: false,
  };
};

// ─── Map PromptIssue to FixableIssueId ───

const ISSUE_MESSAGE_TO_GROUP: Array<{ pattern: RegExp; group: string }> = [
  { pattern: /camera.*lens|lens.*conflict/i, group: 'camera_lens' },
  { pattern: /camera.*angle|perspective.*conflict|angle.*conflict/i, group: 'camera_perspective' },
  { pattern: /shot.*scale|shot.*type|close-up.*wide/i, group: 'camera_shot_type' },
  { pattern: /color.*temperature|warm.*cold|cold.*warm/i, group: 'color_temperature' },
  { pattern: /render.*conflict|photorealistic.*cartoon/i, group: 'render_style' },
  { pattern: /composition.*conflict|rule of thirds.*centered/i, group: 'composition_primary' },
  { pattern: /focus.*conflict|shallow.*deep|tack.*sharp.*soft/i, group: 'dof_mode' },
  { pattern: /film.*stock/i, group: 'film_stock' },
  { pattern: /lighting.*conflict|light.*style/i, group: 'light_style' },
  { pattern: /time.*conflict|night.*day|dawn.*dusk/i, group: 'time_of_day' },
  { pattern: /weather.*conflict|sunny.*rain/i, group: 'weather' },
  { pattern: /style.*conflict|minimalist.*maximalist|vintage.*futuristic/i, group: 'render_style' },
  { pattern: /color.*conflict|black.*white.*vibrant|rich.*desaturated/i, group: 'lut_preset' },
  { pattern: /environment.*conflict|indoor.*outdoor|urban.*rural/i, group: 'camera_lens' }, // fallback
  { pattern: /framing.*conflict|vertical.*horizontal/i, group: 'aspect_ratio' },
];

/**
 * Given a PromptIssue (from the text analyzer), try to map it to a token group
 * so the fix can be applied at the state level.
 */
export const mapIssueToTokenGroup = (issue: PromptIssue): string | null => {
  const msg = `${issue.message} ${issue.reason || ''}`;
  for (const { pattern, group } of ISSUE_MESSAGE_TO_GROUP) {
    if (pattern.test(msg)) return group;
  }
  return null;
};

/**
 * Derive a deterministic FixableIssueId from a PromptIssue.
 */
export const deriveFixableIssueId = (issue: PromptIssue): FixableIssueId => {
  const group = mapIssueToTokenGroup(issue);
  if (group && group in TOKEN_GROUP_TO_ISSUE_ID) {
    return TOKEN_GROUP_TO_ISSUE_ID[group as keyof typeof TOKEN_GROUP_TO_ISSUE_ID];
  }
  if (issue.type === 'duplicate') return 'duplicate_token';
  if (issue.type === 'weak') return 'prompt_text_issue';
  return 'prompt_text_issue';
};
